import React from 'react';
import Modal from './ModalTrue';

export type ClaimBreakdownEntry = {
  claimId: string;
  claimRef: string;
  willCharge: boolean;
  amount: number;
  // 'FREE_AGE_RINGFENCED' is the 70+ cancellation ring-fence
  // (cancellation_payment_service._classify_claim): the client is 70+, or
  // their date of birth cannot be read at all, so the fee is suppressed and a
  // manual review task is raised instead of an invoice. It was missing from
  // this union while the backend was already able to emit it.
  reasonCode:
    | 'CHARGE_OLD_LOC_SENT'
    | 'FREE_WITHIN_GRACE'
    | 'FREE_LOC_NOT_SENT'
    | 'FREE_OUTSIDE_REDRESS_SCHEME'
    | 'FREE_AGE_RINGFENCED'
    | 'FREE_PRIOR_GRACE_CANCELLATION';
  reasonText: string;
  evidence: Record<string, unknown>;
};

type Props = {
  breakdown: ClaimBreakdownEntry[];
  chargeableCount: number;
  estimatedAmount: number;
  currency: string;
  onBack: () => void;
  onContinue: () => void;
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  maxWidth: 560,
  width: '100%',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '12px 0',
  borderBottom: '1px solid #e2e8f0',
  gap: 12,
};

const chargeBadgeStyle: React.CSSProperties = {
  background: '#fef3c7',
  color: '#92400e',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const freeBadgeStyle: React.CSSProperties = {
  ...chargeBadgeStyle,
  background: '#e2e8f0',
  color: '#475569',
};

// Reuses the amber charge palette (not the grey free one) because a fee may
// well follow the 70+ review call — this is not the same as "free".
const ringfenceBadgeStyle: React.CSSProperties = {
  ...chargeBadgeStyle,
  background: '#fde68a',
  color: '#78350f',
  border: '1px solid #b45309',
};

const reasonTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
  marginTop: 4,
};

const totalsStyle: React.CSSProperties = {
  marginTop: 16,
  paddingTop: 12,
  borderTop: '2px solid #cbd5e1',
  fontSize: 14,
  color: '#0f172a',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 20,
  justifyContent: 'flex-end',
};

const buttonBase: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
};

function formatMoney(amount: number, currency: string): string {
  const symbol = currency === 'GBP' ? '£' : `${currency} `;
  return `${symbol}${amount.toFixed(2)}`;
}

export const ChargeReviewModal: React.FC<Props> = ({
  breakdown,
  chargeableCount,
  estimatedAmount,
  currency,
  onBack,
  onContinue,
}) => {
  const ringfencedCount = breakdown.filter(
    (entry) => entry.reasonCode === 'FREE_AGE_RINGFENCED'
  ).length;
  const freeCount = breakdown.length - chargeableCount - ringfencedCount;
  return (
    <Modal onClose={onBack}>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: 18, color: '#0f172a' }}>
            Review charges
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#475569' }}>
            Cancelling {breakdown.length} claim{breakdown.length === 1 ? '' : 's'}.
            Please check the charge breakdown before continuing.
          </p>

          <div>
            {breakdown.map((entry) => {
              const ringfenced = entry.reasonCode === 'FREE_AGE_RINGFENCED';
              const wouldHaveCharged = Boolean(entry.evidence?.wouldHaveCharged);
              // The backend's reasonText ("...ring-fenced for manual review")
              // doesn't itself say the claim would otherwise be chargeable —
              // append a short clause so staff don't read the row as simply free.
              const displayReasonText =
                ringfenced && wouldHaveCharged && !/chargeable/i.test(entry.reasonText)
                  ? `${entry.reasonText} — this claim would otherwise be chargeable.`
                  : entry.reasonText;
              return (
                <div key={entry.claimId} style={rowStyle}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{entry.claimRef}</div>
                    <div style={reasonTextStyle}>{displayReasonText}</div>
                  </div>
                  {ringfenced ? (
                    <span
                      style={ringfenceBadgeStyle}
                      role="status"
                      aria-label={`${entry.claimRef}: fee held for over-70 review`}
                    >
                      70+ REVIEW
                    </span>
                  ) : entry.willCharge ? (
                    <span
                      style={chargeBadgeStyle}
                      role="status"
                      aria-label={`${entry.claimRef}: will be charged`}
                    >
                      CHARGE
                    </span>
                  ) : (
                    <span
                      style={freeBadgeStyle}
                      role="status"
                      aria-label={`${entry.claimRef}: free to cancel`}
                    >
                      FREE
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div style={totalsStyle}>
            <div style={{ fontWeight: 600 }}>
              {`Total chargeable: ${chargeableCount} claim${chargeableCount === 1 ? '' : 's'}` +
                (chargeableCount > 0 ? ` · ${formatMoney(estimatedAmount, currency)}` : '')}
            </div>
            {ringfencedCount > 0 && (
              <div style={{ color: '#92400e', marginTop: 4, fontWeight: 600 }}>
                {`Held for 70+ review: ${ringfencedCount} claim${ringfencedCount === 1 ? '' : 's'} — fee decided on the call`}
              </div>
            )}
            <div style={{ color: '#475569', marginTop: 4 }}>
              {`Free to cancel: ${freeCount} claim${freeCount === 1 ? '' : 's'}`}
            </div>
          </div>

          <div style={buttonRowStyle}>
            <button
              type="button"
              onClick={onBack}
              style={{ ...buttonBase, background: '#f1f5f9', color: '#334155' }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={onContinue}
              style={{ ...buttonBase, background: '#2563eb', color: '#fff' }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
